import React, { createContext, useCallback, useContext, useState, useRef } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * Stylized async confirm dialog (replaces window.confirm where used).
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: "Șterge?", body: "..." , confirmText: "DA", danger: true });
 *   if (ok) {...}
 */

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "Confirmare",
  body: "Ești sigur?",
  confirmText: "DA",
  cancelText: "NU",
  danger: false,
  icon: null,
};

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null); // null = closed
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpts({ ...DEFAULTS, ...options });
    });
  }, []);

  const close = (result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setOpts(null);
    setBusy(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!opts} onOpenChange={(v) => !v && !busy && close(false)}>
        <DialogContent className="max-w-md" data-testid="app-confirm-dialog">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${opts?.danger ? "text-red-700" : "text-zinc-800"}`}>
              {opts?.icon ? <span className="text-xl">{opts.icon}</span> : (opts?.danger ? "⚠️" : "❓")}
              {opts?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {typeof opts?.body === "string" ? (
              <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">{opts.body}</p>
            ) : (
              opts?.body
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => close(false)}
              disabled={busy}
              className="border-zinc-300 px-8"
              data-testid="app-confirm-no"
            >
              ✗ {opts?.cancelText || "NU"}
            </Button>
            <Button
              onClick={() => { setBusy(true); close(true); }}
              disabled={busy}
              className={`${opts?.danger ? "bg-red-600 hover:bg-red-700" : "bg-[var(--primary)] hover:opacity-90"} px-8 font-bold`}
              data-testid="app-confirm-yes"
            >
              ✓ {opts?.confirmText || "DA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to window.confirm if Provider not mounted (e.g. unit tests)
    return (opts) => Promise.resolve(window.confirm(opts?.body || opts?.title || "Confirmi?"));
  }
  return ctx;
}
